import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { notify } from '@/core/notifications/notify';
import ConfirmationModal from '@/components/ConfirmationModal';
import {
  Shield,
  Link2,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Download,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  AlertTriangle,
  Zap,
  PlayCircle,
  ChevronDown,
  Hash,
  BarChart3,
  FileText,
} from 'lucide-react';

interface AuditEntry {
  id: number;
  hash: string;
  previousHash: string;
  timestamp: string;
  action: string;
  resource: string;
  resourceId?: number;
  userId: number;
  companyId?: number;
  nonce: number;
  isVerified: boolean;
  metadata?: {
    changes?: string;
    previousValues?: any;
    newValues?: any;
    ipAddress?: string;
    userAgent?: string;
    sessionId?: string;
  };
  data?: any;
  eventType?: string;
  priority?: string;
  description?: string;
  actor_name?: string;
}

interface BlockchainStats {
  blockchain?: {
    totalBlocks: number;
    totalEntries: number;
    currentBlockHeight: number;
    averageBlockTime: number;
    networkStatus: string;
    lastBlockTimestamp: string;
  };
  integrity?: {
    lastVerification: string;
    integrityStatus: string;
    verificationCount: number;
    failedVerifications: number;
  };
  entries?: {
    totalByType: Record<string, number>;
    totalByPriority: Record<string, number>;
  };
  // Flat stats fallback
  totalEntries?: number;
  chainLength?: number;
  isValid?: boolean;
  lastVerified?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const EVENT_TYPES = [
  { value: '', label: 'All Event Types' },
  { value: 'user_action', label: 'User Action' },
  { value: 'system_event', label: 'System Event' },
  { value: 'data_change', label: 'Data Change' },
  { value: 'security_incident', label: 'Security Incident' },
  { value: 'compliance_check', label: 'Compliance Check' },
  { value: 'access_control', label: 'Access Control' },
];

const PRIORITIES = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const priorityColor = (p: string) => {
  switch (p) {
    case 'critical': return 'bg-red-100 text-red-800';
    case 'high': return 'bg-orange-100 text-orange-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'low': return 'bg-green-100 text-green-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const eventTypeLabel = (t: string) => {
  const m = EVENT_TYPES.find(e => e.value === t);
  return m ? m.label : t?.replace(/_/g, ' ') ?? 'Unknown';
};

export default function BlockchainAuditDashboard() {
  const { hasPermission } = useAuth();

  const [stats, setStats] = useState<BlockchainStats | null>(null);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [chainValid, setChainValid] = useState<boolean | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEventType, setFilterEventType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, pages: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/blockchain-audit/stats', { headers: authHeaders() });
      const json = res.ok ? await res.json() : null;
      setStats(json?.data || json || null);
    } catch (err) {
      console.error('Error fetching blockchain stats', err);
      setStats(null);
    }
  }, []);

  const fetchTrail = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (filterEventType) params.append('eventType', filterEventType);
      if (filterPriority) params.append('priority', filterPriority);
      if (filterFromDate) params.append('fromDate', filterFromDate);
      if (filterToDate) params.append('toDate', filterToDate);
      if (searchTerm) params.append('search', searchTerm);

      const res = await fetch(`/api/blockchain-audit/trail?${params}`, { headers: authHeaders() });
      if (res.ok) {
        const json = await res.json();
        const data = json.data;
        if (Array.isArray(data)) {
          setEntries(data);
          if (json.pagination) setPagination(json.pagination);
        } else if (data?.entries) {
          setEntries(data.entries);
          if (data.pagination) setPagination(data.pagination);
        } else {
          setEntries([]);
        }
      } else {
        setEntries([]);
        if (res.status !== 404) notify.error('Failed to load audit trail');
      }
    } catch (err) {
      console.error('Error fetching audit trail', err);
      setEntries([]);
      notify.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filterEventType, filterPriority, filterFromDate, filterToDate, searchTerm]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchTrail(); }, [fetchTrail]);

  const handleInitialize = async () => {
    setInitializing(true);
    try {
      const res = await fetch('/api/blockchain-audit/initialize', { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        notify.success('Blockchain audit system initialized');
        fetchStats();
        fetchTrail();
      } else {
        let message = 'Failed to initialize';
        try { const json = await res.json(); message = json.message || message; } catch {}
        notify.error(message);
      }
    } catch (err) {
      console.error('Error initializing blockchain', err);
      notify.error('Failed to initialize blockchain');
    } finally {
      setInitializing(false);
    }
  };

  const handleVerifyChain = async () => {
    setVerifying(true);
    try {
      const res = await fetch('/api/blockchain-audit/verify', { headers: authHeaders() });
      const json = res.ok ? await res.json() : null;
      if (json) {
        const valid = json.data?.isValid ?? json.data?.integrityStatus === 'valid';
        setChainValid(valid);
        notify[valid ? 'success' : 'error'](valid ? 'Chain integrity verified' : 'Chain integrity compromised!');
        fetchStats();
      } else {
        notify.error('Verification failed — service unavailable');
      }
    } catch (err) {
      console.error('Error verifying chain', err);
      notify.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleCreateCheckpoint = async () => {
    try {
      const res = await fetch('/api/blockchain-audit/checkpoint', { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        notify.success('Checkpoint created');
        fetchStats();
      } else {
        let message = 'Failed to create checkpoint';
        try { const json = await res.json(); message = json.message || message; } catch {}
        notify.error(message);
      }
    } catch (err) {
      console.error('Error creating checkpoint', err);
      notify.error('Failed to create checkpoint');
    }
  };

  const handleExport = async (format: string) => {
    setShowExportMenu(false);
    try {
      const params = new URLSearchParams({ format });
      if (filterFromDate) params.append('fromDate', filterFromDate);
      if (filterToDate) params.append('toDate', filterToDate);
      if (filterEventType) params.append('eventTypes', filterEventType);

      const res = await fetch(`/api/blockchain-audit/export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'X-CSRF-Token': getCsrfToken() },
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_trail_${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
        notify.success(`Exported as ${format.toUpperCase()}`);
      } else {
        notify.error('Export failed');
      }
    } catch (err) {
      console.error('Error exporting audit trail', err);
      notify.error('Export failed');
    }
  };

  const handleVerifyEntry = async (entryId: number) => {
    try {
      const res = await fetch(`/api/blockchain-audit/verify/${entryId}`, { method: 'POST', headers: authHeaders() });
      if (res.ok) {
        notify.success('Entry verified');
        if (selectedEntry?.id === entryId) {
          setSelectedEntry({ ...selectedEntry, isVerified: true });
        }
        fetchTrail();
      } else {
        let message = 'Entry verification failed';
        try { const json = await res.json(); message = json.message || message; } catch {}
        notify.error(message);
      }
    } catch (err) {
      console.error('Error verifying entry', err);
      notify.error('Entry verification failed');
    }
  };

  // Derive display stats
  const totalEntries = stats?.blockchain?.totalEntries ?? stats?.totalEntries ?? entries.length;
  const chainStatus = stats?.integrity?.integrityStatus ?? (chainValid === true ? 'valid' : chainValid === false ? 'compromised' : 'unknown');
  const lastVerification = stats?.integrity?.lastVerification ?? stats?.lastVerified;
  const verificationCount = stats?.integrity?.verificationCount ?? 0;

  const formatDate = (d: string | undefined) => {
    if (!d) return '-';
    return new Date(d).toLocaleString();
  };

  const truncateHash = (h: string | undefined) => {
    if (!h) return '-';
    if (h.length <= 16) return h;
    return h.slice(0, 8) + '...' + h.slice(-8);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link2 className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Blockchain Audit Trail</h1>
            <p className="text-gray-500">Immutable, cryptographically sealed audit records</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleInitialize} disabled={initializing} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm">
            <PlayCircle className="w-4 h-4" /> {initializing ? 'Initializing...' : 'Initialize'}
          </button>
          <button onClick={handleVerifyChain} disabled={verifying} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm">
            <Shield className="w-4 h-4" /> {verifying ? 'Verifying...' : 'Verify Chain'}
          </button>
          <button onClick={handleCreateCheckpoint} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm">
            <Zap className="w-4 h-4" /> Checkpoint
          </button>
          <div className="relative">
            <button onClick={() => setShowExportMenu(!showExportMenu)} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">
              <Download className="w-4 h-4" /> Export <ChevronDown className="w-3 h-3" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                {['json', 'csv', 'pdf'].map(fmt => (
                  <button key={fmt} onClick={() => handleExport(fmt)} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50">{fmt.toUpperCase()}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Entries</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{totalEntries}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg"><FileText className="w-6 h-6 text-indigo-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Chain Status</p>
              <div className="flex items-center gap-2 mt-1">
                {chainStatus === 'valid' ? <CheckCircle className="w-5 h-5 text-green-500" /> : chainStatus === 'compromised' ? <XCircle className="w-5 h-5 text-red-500" /> : <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                <span className={`text-lg font-bold ${chainStatus === 'valid' ? 'text-green-700' : chainStatus === 'compromised' ? 'text-red-700' : 'text-yellow-700'}`}>
                  {chainStatus.charAt(0).toUpperCase() + chainStatus.slice(1)}
                </span>
              </div>
            </div>
            <div className="p-3 bg-green-50 rounded-lg"><Shield className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Last Verification</p>
              <p className="text-sm font-semibold text-gray-900 mt-1">{lastVerification ? formatDate(lastVerification) : 'Never'}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg"><Clock className="w-6 h-6 text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Verifications</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{verificationCount}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg"><BarChart3 className="w-6 h-6 text-purple-600" /></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search entries..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
            </div>
          </div>
          <div className="min-w-[160px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Event Type</label>
            <select value={filterEventType} onChange={e => { setFilterEventType(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
              {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div className="min-w-[130px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">Priority</label>
            <select value={filterPriority} onChange={e => { setFilterPriority(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
            <input type="date" value={filterFromDate} onChange={e => { setFilterFromDate(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div className="min-w-[150px]">
            <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
            <input type="date" value={filterToDate} onChange={e => { setFilterToDate(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500" />
          </div>
          <button onClick={() => { setSearchTerm(''); setFilterEventType(''); setFilterPriority(''); setFilterFromDate(''); setFilterToDate(''); setPagination(p => ({ ...p, page: 1 })); }}
            className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50">
            Clear
          </button>
          <button onClick={() => fetchTrail()} className="px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-300 rounded-lg hover:bg-indigo-50">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Audit Trail Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : entries.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Link2 className="w-12 h-12 mb-3" />
            <p className="text-lg font-medium">No audit entries found</p>
            <p className="text-sm">Initialize the blockchain or adjust your filters</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Timestamp</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Event Type</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Priority</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Action</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Resource</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Hash</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Verified</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatDate(entry.timestamp)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {eventTypeLabel(entry.eventType || entry.action || '')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${priorityColor(entry.priority || 'medium')}`}>
                        {(entry.priority || 'medium').charAt(0).toUpperCase() + (entry.priority || 'medium').slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{entry.action || '-'}</td>
                    <td className="px-4 py-3 text-gray-700">{entry.resource || '-'}</td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono text-gray-600">{truncateHash(entry.hash)}</code>
                    </td>
                    <td className="px-4 py-3">
                      {entry.isVerified ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-300" />
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedEntry(entry)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" title="View details">
                          <Eye className="w-4 h-4" />
                        </button>
                        {!entry.isVerified && (
                          <button onClick={() => handleVerifyEntry(entry.id)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Verify entry">
                            <Shield className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))} disabled={pagination.page <= 1}
                className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                const start = Math.max(1, Math.min(pagination.page - 2, pagination.pages - 4));
                const pageNum = start + i;
                if (pageNum > pagination.pages) return null;
                return (
                  <button key={pageNum} onClick={() => setPagination(p => ({ ...p, page: pageNum }))}
                    className={`w-8 h-8 rounded-lg text-sm ${pagination.page === pageNum ? 'bg-indigo-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`}>
                    {pageNum}
                  </button>
                );
              })}
              <button onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))} disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEntry(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <Hash className="w-5 h-5" /> Audit Entry Detail
              </h2>
              <button onClick={() => setSelectedEntry(null)} className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(85vh-64px)] p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Entry ID</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">{selectedEntry.id}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Timestamp</p>
                  <p className="text-sm text-gray-900 mt-0.5">{formatDate(selectedEntry.timestamp)}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Action</p>
                  <p className="text-sm text-gray-900 mt-0.5">{selectedEntry.action || '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Resource</p>
                  <p className="text-sm text-gray-900 mt-0.5">{selectedEntry.resource || '-'}{selectedEntry.resourceId ? ` #${selectedEntry.resourceId}` : ''}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Event Type</p>
                  <span className="inline-flex mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {eventTypeLabel(selectedEntry.eventType || selectedEntry.action || '')}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Priority</p>
                  <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${priorityColor(selectedEntry.priority || 'medium')}`}>
                    {(selectedEntry.priority || 'medium').charAt(0).toUpperCase() + (selectedEntry.priority || 'medium').slice(1)}
                  </span>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">User ID</p>
                  <p className="text-sm text-gray-900 mt-0.5">{selectedEntry.userId ?? '-'}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase">Verified</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {selectedEntry.isVerified ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-gray-300" />}
                    <span className="text-sm">{selectedEntry.isVerified ? 'Verified' : 'Unverified'}</span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Hash</p>
                <code className="block text-xs bg-gray-100 p-3 rounded-lg font-mono text-gray-700 break-all">{selectedEntry.hash || '-'}</code>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Previous Hash</p>
                <code className="block text-xs bg-gray-100 p-3 rounded-lg font-mono text-gray-700 break-all">{selectedEntry.previousHash || '-'}</code>
              </div>

              {selectedEntry.metadata && Object.keys(selectedEntry.metadata).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Metadata</p>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(selectedEntry.metadata, null, 2)}
                  </pre>
                </div>
              )}

              {selectedEntry.data && (
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Data</p>
                  <pre className="text-xs bg-gray-100 p-3 rounded-lg font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(selectedEntry.data, null, 2)}
                  </pre>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                {!selectedEntry.isVerified && (
                  <button onClick={() => { handleVerifyEntry(selectedEntry.id); setSelectedEntry(null); }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm">
                    <Shield className="w-4 h-4" /> Verify Entry
                  </button>
                )}
                <button onClick={() => setSelectedEntry(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
